/**
 * @file Hook: useTitleMusicAutoplay
 * @description Custom hook for automatically playing title music with persistence across page reloads
 */

import { useCallback, useEffect, useRef } from 'react';
import { AUDIO_ASSETS } from '../constants/audioAssets';
import { useAudioContext } from '../contexts/AudioContext';
import { useSound } from './useSound';

/**
 * Hook that automatically plays title music and persists it across page reloads
 * @returns Object with play, stop, and sound controls
 */
export function useTitleMusicAutoplay() {
  const { isMusicEnabled, musicVolume } = useAudioContext();

  const [play, { sound, stop }] = useSound(AUDIO_ASSETS.MUSIC.TITLE_SCREEN, {
    category: 'MUSIC',
    loop: true,
    volume: musicVolume,
    soundEnabled: isMusicEnabled,
  });

  const hasStartedRef = useRef(false);

  useEffect(() => {
    if (!sound) return;
    sound.volume(musicVolume);
    sound.mute(!isMusicEnabled);
  }, [sound, musicVolume, isMusicEnabled]);

  const wrappedPlay = useCallback(() => {
    if (sound) {
      // Prevent double playing here too
      if (sound.playing()) return;

      play();
      hasStartedRef.current = true;
      try {
        sessionStorage.setItem('titleMusicStarted', 'true');
      } catch (e) {
        console.error('Failed to set session storage:', e);
      }
    }
  }, [sound, play]);

  const wrappedStop = useCallback(() => {
    if (sound) {
      stop();
      hasStartedRef.current = false;
      try {
        sessionStorage.removeItem('titleMusicStarted');
      } catch (e) {
        console.error('Failed to remove from session storage:', e);
      }
    }
  }, [sound, stop]);

  return {
    play: wrappedPlay,
    stop: wrappedStop,
    sound,
  };
}
